import { useState, useEffect, useContext } from "react";
import { useNavigate, Link } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import axios from "axios";
import toast from "react-hot-toast";
import { API_BASE } from "../config";
import {
  FaUser,
  FaPhone,
  FaMapMarkerAlt,
  FaCreditCard,
  FaLeaf,
  FaTimes,
  FaBriefcase,
  FaClipboardList,
  FaAppleAlt,
  FaFileAlt,
  FaLock,
} from "react-icons/fa";
import "./Profile.css";

const Profile = () => {
  const { user, token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("contact");
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState({
    name: "",
    email: "",
    phone: "",
    address: { street: "", city: "", state: "", zip: "" },
    payment: { cardName: "", cardNumber: "", expiry: "" },
    dietary: { preference: "none", allergies: [] },
  });

  const [allergyInput, setAllergyInput] = useState("");
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [changingPassword, setChangingPassword] = useState(false);

  const API = `${API_BASE}/api/profile`;

  const dietaryOptions = [
    "none",
    "vegetarian",
    "vegan",
    "pescatarian",
    "keto",
    "halal",
    "kosher",
    "gluten-free",
  ];

  const commonAllergens = [
    "Peanuts",
    "Tree Nuts",
    "Milk",
    "Eggs",
    "Wheat",
    "Soy",
    "Fish",
    "Beef",
    "Sesame",
    "Gluten",
    "Lactose",
    "Corn",
  ];

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    fetchProfile();
  }, [user]);

  const fetchProfile = async () => {
    try {
      const res = await axios.get(API, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProfile({
        name: res.data.name || "",
        email: res.data.email || "",
        phone: res.data.phone || "",
        address: res.data.address || { street: "", city: "", state: "", zip: "" },
        payment: res.data.payment || { cardName: "", cardNumber: "", expiry: "" },
        dietary: res.data.dietary || { preference: "none", allergies: [] },
      });
    } catch (error) {
      toast.error("Failed to load profile");
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const res = await axios.put(API, profile, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProfile({
        name: res.data.name,
        email: res.data.email,
        phone: res.data.phone || "",
        address: res.data.address || { street: "", city: "", state: "", zip: "" },
        payment: res.data.payment || { cardName: "", cardNumber: "", expiry: "" },
        dietary: res.data.dietary || { preference: "none", allergies: [] },
      });
      toast.success("Profile updated successfully!");
    } catch (error) {
      toast.error("Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const updateAddress = (field, value) => {
    setProfile({ ...profile, address: { ...profile.address, [field]: value } });
  };

  const updatePayment = (field, value) => {
    setProfile({ ...profile, payment: { ...profile.payment, [field]: value } });
  };

  const toggleAllergy = (allergy) => {
    const current = profile.dietary.allergies;
    const updated = current.includes(allergy)
      ? current.filter((a) => a !== allergy)
      : [...current, allergy];
    setProfile({ ...profile, dietary: { ...profile.dietary, allergies: updated } });
  };

  const addCustomAllergy = () => {
    const trimmed = allergyInput.trim();
    if (trimmed && !profile.dietary.allergies.includes(trimmed)) {
      setProfile({
        ...profile,
        dietary: { ...profile.dietary, allergies: [...profile.dietary.allergies, trimmed] },
      });
    }
    setAllergyInput("");
  };

  const handleChangePassword = async () => {
    const { currentPassword, newPassword, confirmPassword } = passwordForm;
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error("Please fill in all password fields");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("New password and confirmation do not match");
      return;
    }

    setChangingPassword(true);
    try {
      await axios.put(
        `${API}/password`,
        { currentPassword, newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Password updated successfully!");
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update password");
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="profile-page">
      <div className="profile-container">
        <div className="profile-sidebar">
          <div className="profile-avatar">
            <FaUser className="avatar-icon" />
            <h3>{profile.name}</h3>
            <p>{profile.email}</p>
          </div>
          <ul className="profile-tabs">
            <li
              className={activeTab === "contact" ? "active" : ""}
              onClick={() => setActiveTab("contact")}
            >
              <FaPhone /> Contact Details
            </li>
            <li
              className={activeTab === "address" ? "active" : ""}
              onClick={() => setActiveTab("address")}
            >
              <FaMapMarkerAlt /> Address
            </li>
            <li
              className={activeTab === "payment" ? "active" : ""}
              onClick={() => setActiveTab("payment")}
            >
              <FaCreditCard /> Payment Info
            </li>
            <li
              className={activeTab === "dietary" ? "active" : ""}
              onClick={() => setActiveTab("dietary")}
            >
              <FaLeaf /> Dietary Preferences
            </li>
            <li
              className={activeTab === "security" ? "active" : ""}
              onClick={() => setActiveTab("security")}
            >
              <FaLock /> Change Password
            </li>
          </ul>

          <p className="profile-tabs-divider">More</p>
          <ul className="profile-tabs">
            <li>
              <Link to="/jobs"><FaBriefcase /> Careers</Link>
            </li>
            <li>
              <Link to="/orders"><FaClipboardList /> Orders</Link>
            </li>
            <li>
              <Link to="/diet-plan"><FaAppleAlt /> Diet Plan</Link>
            </li>
            <li>
              <Link to="/my-applications"><FaFileAlt /> My Applications</Link>
            </li>
          </ul>
        </div>

        <div className="profile-content">
          {activeTab === "contact" && (
            <div className="profile-section">
              <h2>Contact Details</h2>
              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  value={profile.name}
                  onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                  placeholder="Your full name"
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input type="email" value={profile.email} disabled />
                <small>Email cannot be changed</small>
              </div>
              <div className="form-group">
                <label>Phone Number</label>
                <input
                  type="tel"
                  value={profile.phone}
                  onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                  placeholder="Enter your phone number"
                />
              </div>
            </div>
          )}

          {activeTab === "address" && (
            <div className="profile-section">
              <h2>Delivery Address</h2>
              <div className="form-group">
                <label>Street Address</label>
                <input
                  type="text"
                  value={profile.address.street}
                  onChange={(e) => updateAddress("street", e.target.value)}
                  placeholder="123 Main Street"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    value={profile.address.city}
                    onChange={(e) => updateAddress("city", e.target.value)}
                    placeholder="City"
                  />
                </div>
                <div className="form-group">
                  <label>State</label>
                  <input
                    type="text"
                    value={profile.address.state}
                    onChange={(e) => updateAddress("state", e.target.value)}
                    placeholder="State"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>ZIP Code</label>
                <input
                  type="text"
                  value={profile.address.zip}
                  onChange={(e) => updateAddress("zip", e.target.value)}
                  placeholder="ZIP Code"
                />
              </div>
            </div>
          )}

          {activeTab === "payment" && (
            <div className="profile-section">
              <h2>Payment Information</h2>
              <div className="form-group">
                <label>Name on Card</label>
                <input
                  type="text"
                  value={profile.payment.cardName}
                  onChange={(e) => updatePayment("cardName", e.target.value)}
                  placeholder="Name on card"
                />
              </div>
              <div className="form-group">
                <label>Card Number</label>
                <input
                  type="text"
                  value={profile.payment.cardNumber}
                  onChange={(e) => updatePayment("cardNumber", e.target.value)}
                  placeholder="XXXX XXXX XXXX XXXX"
                  maxLength={19}
                />
              </div>
              <div className="form-group">
                <label>Expiry Date</label>
                <input
                  type="text"
                  value={profile.payment.expiry}
                  onChange={(e) => updatePayment("expiry", e.target.value)}
                  placeholder="MM/YY"
                  maxLength={5}
                />
              </div>
              <p className="allergy-hint">Your card number is stored masked and the CVV is never saved.</p>
            </div>
          )}

          {activeTab === "dietary" && (
            <div className="profile-section">
              <h2>Dietary Preferences</h2>
              <div className="form-group">
                <label>Diet Type</label>
                <select
                  className="diet-select"
                  value={profile.dietary.preference}
                  onChange={(e) =>
                    setProfile({
                      ...profile,
                      dietary: { ...profile.dietary, preference: e.target.value },
                    })
                  }
                >
                  {dietaryOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt.charAt(0).toUpperCase() + opt.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Food Allergies</label>
                <p className="allergy-hint">Select common allergens or add your own</p>
                <div className="allergen-grid">
                  {commonAllergens.map((allergen) => (
                    <button
                      key={allergen}
                      type="button"
                      className={`allergen-chip ${
                        profile.dietary.allergies.includes(allergen) ? "selected" : ""
                      }`}
                      onClick={() => toggleAllergy(allergen)}
                    >
                      {allergen}
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Add Custom Allergy</label>
                <div className="custom-allergy-row">
                  <input
                    type="text"
                    value={allergyInput}
                    onChange={(e) => setAllergyInput(e.target.value)}
                    placeholder="Type an allergy and press Add"
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustomAllergy())}
                  />
                  <button type="button" className="add-allergy-btn" onClick={addCustomAllergy}>
                    Add
                  </button>
                </div>
              </div>

              {profile.dietary.allergies.length > 0 && (
                <div className="selected-allergies">
                  <label>Your Allergies:</label>
                  <div className="allergy-tags">
                    {profile.dietary.allergies.map((allergy) => (
                      <span key={allergy} className="allergy-tag">
                        {allergy}
                        <FaTimes className="remove-icon" onClick={() => toggleAllergy(allergy)} />
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === "security" && (
            <div className="profile-section">
              <h2>Change Password</h2>
              <div className="form-group">
                <label>Current Password</label>
                <input
                  type="password"
                  value={passwordForm.currentPassword}
                  onChange={(e) =>
                    setPasswordForm({ ...passwordForm, currentPassword: e.target.value })
                  }
                  placeholder="Enter current password"
                />
              </div>
              <div className="form-group">
                <label>New Password</label>
                <input
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e) =>
                    setPasswordForm({ ...passwordForm, newPassword: e.target.value })
                  }
                  placeholder="At least 6 characters"
                />
              </div>
              <div className="form-group">
                <label>Confirm New Password</label>
                <input
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) =>
                    setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })
                  }
                  placeholder="Re-enter new password"
                />
              </div>
              <button
                className="save-btn"
                onClick={handleChangePassword}
                disabled={changingPassword}
              >
                {changingPassword ? "Updating..." : "Update Password"}
              </button>
            </div>
          )}

          {activeTab !== "security" && (
            <button className="save-btn" onClick={handleSave} disabled={loading}>
              {loading ? "Saving..." : "Save Changes"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
